import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, LOGO_URL } from "../lib/api";
import { Lock, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  React.useEffect(() => { document.title = "Reset Password – Cheap4Teach"; }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (password !== confirm) { toast.error("Passwords don't match"); return; }
    if (!token) { toast.error("Missing reset token. Use the link from your email."); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => nav("/login", { replace: true }), 2000);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-16" data-testid="reset-password-page">
      <div className="c4t-card">
        <img src={LOGO_URL} alt="Cheap4Teach" className="mx-auto h-20 logo-blend" />
        <h1 className="text-3xl font-['Fredoka'] font-bold mt-4 text-center">Choose a new password</h1>

        {done ? (
          <div className="mt-6 c4t-card !bg-[#E6FFF7] !shadow-[4px_4px_0_#00E0AA] text-center" data-testid="reset-success">
            <CheckCircle2 className="w-10 h-10 text-[#00E0AA] mx-auto" />
            <p className="font-semibold mt-3">Password updated!</p>
            <p className="text-sm c4t-desc mt-2">Redirecting you to sign in...</p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="text-sm font-semibold flex items-center gap-1.5"><Lock className="w-4 h-4" /> New password</label>
              <div className="relative mt-1">
                <input type={showPw ? "text" : "password"} required value={password} onChange={e => setPassword(e.target.value)}
                  className="c4t-input pr-12" placeholder="At least 6 characters" data-testid="reset-password-input" />
                <button type="button" onClick={() => setShowPw(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#455A64] hover:text-[#FF1F87]"
                  aria-label={showPw ? "Hide password" : "Show password"}
                  data-testid="reset-password-toggle">
                  {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold flex items-center gap-1.5"><Lock className="w-4 h-4" /> Confirm password</label>
              <input type={showPw ? "text" : "password"} required value={confirm} onChange={e => setConfirm(e.target.value)}
                className="c4t-input mt-1" placeholder="Re-enter your password" data-testid="reset-confirm-input" />
            </div>
            <button type="submit" disabled={busy} className="c4t-btn c4t-btn-pink w-full" data-testid="reset-submit-btn">
              {busy ? "Saving..." : "Save new password"}
            </button>
            <Link to="/login" className="block text-center text-sm text-[#118AB2] hover:text-[#FF477E] font-semibold">
              ← Back to sign in
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
